import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminApi } from "@/lib/auth-api";
import {
	deleteVisibilityRule,
	fetchMyVisibilityRules,
	fetchResolveRole,
	fetchVisibilityRules,
	upsertVisibilityRule,
	upsertVisibilityRuleForAllRoles,
} from "./visibility-api";

// Exported so route-guards.ts can reuse the exact same keys from beforeLoad —
// if the guard and the React provider key differ by even a whitespace, they get
// separate cache entries and every guarded navigation triggers a duplicate fetch.
export const VISIBILITY_RULES_QUERY_KEY = (tenantId?: number) =>
	["visibility-rules", "me", { tenantId }] as const;
export const ORG_VISIBILITY_RULES_QUERY_KEY = (tenantId?: number) =>
	["visibility-rules", "me", "org", { tenantId }] as const;
const VISIBILITY_RULES_ADMIN_QUERY_KEY = (tenantId?: number) =>
	["visibility-rules", "admin", { tenantId }] as const;

/**
 * Hook dla zwykłych zalogowanych użytkowników — pobiera reguły dla ich ról z /api/visibility-rules/me.
 * Nie wymaga super_admin. Używany w VisibilityProviderConnected w __root.tsx.
 */
export function useMyVisibilityRules(tenantId?: number, enabled = true) {
	return useQuery({
		queryKey: VISIBILITY_RULES_QUERY_KEY(tenantId),
		queryFn: fetchMyVisibilityRules,
		staleTime: 5 * 60 * 1000,
		retry: false,
		enabled,
		refetchOnWindowFocus: true,
		refetchOnReconnect: true,
	});
}

/**
 * Hook dla zalogowanych użytkowników — pobiera zresolwowane reguły dla ich grup organizacyjnych.
 * Nie wymaga super_admin. Używany obok reguł bazujących na rolach.
 */
export function useMyOrgVisibilityRules(tenantId?: number, enabled = true) {
	return useQuery({
		queryKey: ORG_VISIBILITY_RULES_QUERY_KEY(tenantId),
		queryFn: adminApi.getMyOrgVisibilityRules,
		staleTime: 5 * 60 * 1000,
		retry: false,
		enabled,
		refetchOnWindowFocus: true,
		refetchOnReconnect: true,
	});
}

/**
 * Hook dla super_admin w edytorze widoczności — pobiera wszystkie reguły z /api/admin/visibility-rules.
 * Wymaga super_admin.
 */
export function useVisibilityRules(tenantId?: number, enabled = true) {
	return useQuery({
		queryKey: VISIBILITY_RULES_ADMIN_QUERY_KEY(tenantId),
		queryFn: () => fetchVisibilityRules(),
		staleTime: 5 * 60 * 1000,
		retry: false,
		enabled,
		refetchOnWindowFocus: true,
		refetchOnReconnect: true,
	});
}

export function useUpsertVisibilityRule() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: upsertVisibilityRule,
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["visibility-rules"] }),
	});
}

export function useUpsertVisibilityRuleForAllRoles() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: upsertVisibilityRuleForAllRoles,
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["visibility-rules"] }),
	});
}

export function useDeleteVisibilityRule() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: deleteVisibilityRule,
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["visibility-rules"] }),
	});
}

/**
 * Reguły widoczności JEDNEJ grupy organizacyjnej — dla edytora, gdy super_admin
 * wybierze grupę zamiast roli.
 *
 * Osobny fetch, bo `useMyOrgVisibilityRules` zwraca wyłącznie grupy, do których
 * należy zalogowany użytkownik. Bez tego edytowana grupa pokazywałaby wszystko
 * jako ukryte, a pierwsze Ctrl+klik na nadanym już elemencie próbowałoby nadać
 * grant zamiast go odebrać.
 *
 * Klucz zaczyna się od `visibility-rules`, więc invalidacja w
 * `useUpsertOrgGroupVisibilityRule` (prefix match) odświeża go po każdym toggle.
 */
export const ORG_GROUP_VISIBILITY_QUERY_KEY = (groupId: number | null) =>
	["visibility-rules", "org-group", groupId] as const;

export function useOrgGroupVisibilityRules(groupId: number | null) {
	return useQuery({
		queryKey: ORG_GROUP_VISIBILITY_QUERY_KEY(groupId),
		queryFn: () => adminApi.getOrgGroupVisibility(groupId ?? undefined),
		enabled: groupId != null,
		staleTime: 5 * 60 * 1000,
		retry: false,
	});
}

export function useUpsertOrgGroupVisibilityRule() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: adminApi.upsertOrgGroupVisibility,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["visibility-rules"] });
		},
	});
}

export function useDeleteOrgGroupVisibilityRule() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (params: { groupId: number; elementId: string }) =>
			adminApi.deleteOrgGroupVisibility(params.groupId, params.elementId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["visibility-rules"] });
		},
	});
}

// 5 min staleTime zgodnie z norma repo (useVisibilityRules) — invalidation odbywa sie
// automatycznie przez `useUpsertVisibilityRule` ktory invaliduje `["visibility-rules"]`
// (prefix matching lapie nasz key `["visibility-rules", "resolve", roleSlug]`).
export const VISIBILITY_RESOLVE_QUERY_KEY = (role: string | null) =>
	["visibility-rules", "resolve", role] as const;

export function useRoleResolve(roleSlug: string | null) {
	return useQuery({
		queryKey: VISIBILITY_RESOLVE_QUERY_KEY(roleSlug),
		queryFn: () => fetchResolveRole(roleSlug as string),
		enabled: roleSlug !== null,
		staleTime: 5 * 60 * 1000,
		retry: false,
	});
}
